import { useMemo, useState, type ReactNode } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import {
  Card, Table, Tag, Select, EmptyState, Image, StatCard, EntityAvatar, PageHeader, Tooltip, CodeChip,
  type TagTone,
} from '../../ui';
import {
  ScanBarcode, Warehouse as WarehouseIcon, Store, PackageCheck, Undo2, TriangleAlert,
  CalendarClock, Boxes, ArrowDownRight, ArrowUpRight, Search, ShieldAlert, Package, Layers,
} from 'lucide-react';
import dayjs from 'dayjs';
import { request } from '../../api/client';
import { useDebounce } from '../../hooks/useDebounce';
import { fmtQty, inr2, isRedundantSku } from '../../lib/format';
import { formatIst, formatIstDate } from '../../lib/time';
import { productImage } from '../../lib/imageUrls';
import { PALETTE } from '../../lib/palette';
import { MasterTag } from '../../lib/warehouseLabel';
import { RetailerLink } from '../../components/EntityLinks';

const TXN_COLOR: Record<string, TagTone> = {
  OPENING: 'green', GRN: 'green', TRANSFER_IN: 'cyan', RETURN_IN: 'cyan', ADJUST_IN: 'blue',
  SO: 'red', TRANSFER_OUT: 'gold', RETURN_OUT: 'gold', ADJUST_OUT: 'gold', PROD_IN: 'green', PROD_OUT: 'gold', SAMPLE: 'purple',
};
// Human labels for the raw ledger txn codes (kept in step with StockHistoryPage) — so the timeline
// reads "Sales Dispatch" instead of the internal "SO", "Sales Return In" instead of "RETURN_IN", etc.
const TXN_LABEL: Record<string, string> = {
  OPENING: 'Opening Stock', GRN: 'Goods Receipt (GRN)', SO: 'Sales Dispatch',
  TRANSFER_IN: 'Transfer In', TRANSFER_OUT: 'Transfer Out',
  ADJUST_IN: 'Adjustment In', ADJUST_OUT: 'Adjustment Out',
  RETURN_IN: 'Sales Return In', RETURN_OUT: 'Return Out',
  PROD_IN: 'Production In', PROD_OUT: 'Production Consume', SAMPLE: 'Sample Issue',
};
const txnLabel = (v: string) => TXN_LABEL[v] ?? (v ?? '').replace(/_/g, ' ');
const STATUS_META: Record<string, { tone: TagTone; label: string }> = {
  AVAILABLE: { tone: 'green', label: 'Available' },
  QC_HOLD: { tone: 'gold', label: 'QC hold' },
  EXPIRED: { tone: 'red', label: 'Expired' },
  BLOCKED: { tone: 'red', label: 'Blocked' },
};
const statusMeta = (s?: string | null) =>
  STATUS_META[s ?? ''] ?? { tone: 'neutral' as TagTone, label: (s ?? '').replace(/_/g, ' ') || '—' };

/**
 * A ROW'S REAL CONDITION — the stored flag unless the date has already overruled it.
 *
 * A warehouse can hold a lot past its printed date while its record still reads AVAILABLE (nobody ran
 * the sweep). The date wins: the expiry column is already red on such a row, and a panel that kept
 * repeating the stale flag contradicted the table beside it.
 */
const effectiveStatus = (r: any) => (expiryState(r?.expDate, r?.status) === 'expired' ? 'EXPIRED' : (r?.status ?? 'AVAILABLE'));

/** Why a lot may not be sold, phrased to follow the word "No". */
const NOT_SELLABLE: Record<string, string> = { EXPIRED: 'expired', QC_HOLD: 'on QC hold', BLOCKED: 'blocked' };

/**
 * CAN THIS STOCK BE SOLD — answered in words.
 *
 * `stock_batches.status` is a DISPOSITION ("may this be sold"), never a quantity, so a lot that has
 * been dispatched down to nothing keeps AVAILABLE. Printed as a green "Available" tag next to a
 * column headed "Available" showing "none left", it read as a flat contradiction — two meanings of
 * one word, side by side. The column now asks a question and the tag answers it, and a row holding
 * nothing says THAT rather than ruling on stock that is not there.
 */
function sellableVerdict(status: string | null | undefined, qty: any, expDate?: string | null): {
  tone: TagTone; label: string; note?: string;
} {
  if (!(Number(qty ?? 0) > 0)) return { tone: 'neutral', label: 'Emptied' };
  const st = status ?? 'AVAILABLE';
  // The DATE counts as much as the flag — the same "expired = flagged OR date passed" rule the expiry
  // column is coloured by. Reading only the flag let a lot that ran out in Jan 2020 answer "Yes" next
  // to its own date printed in red.
  if (expiryState(expDate, st) === 'expired') {
    return {
      tone: 'red',
      label: 'No · expired',
      note: st === 'AVAILABLE'
        ? 'Past its expiry date, though this warehouse still has the lot flagged AVAILABLE — worth correcting in stock.'
        : undefined,
    };
  }
  if (st === 'AVAILABLE') return { tone: 'green', label: 'Yes' };
  return { tone: statusMeta(st).tone, label: `No · ${NOT_SELLABLE[st] ?? statusMeta(st).label}` };
}

const dash = <span className="text-subtle">—</span>;

/**
 * A lot the returns flow minted for stock that came BACK (`RET-<condition>-<returnNo>`, and/or with
 * its printed parent recorded in `source_batch_no`). Either signal is enough: older returned lots
 * predate the parent link, and the prefix is issued by the returns code itself.
 */
const isReturnLot = (r: any) => !!r?.sourceBatchNo || /^RET-/i.test(String(r?.batchNo ?? ''));

/** Expiry read the way the warehouse reads it: a passed date is expired even before anyone flags it. */
function expiryState(expDate?: string | null, status?: string | null) {
  if (status === 'EXPIRED') return 'expired' as const;
  if (!expDate) return 'none' as const;
  const d = dayjs(expDate);
  if (!d.isValid()) return 'none' as const;
  if (d.isBefore(dayjs(), 'day')) return 'expired' as const;
  return d.diff(dayjs(), 'day') <= 90 ? ('soon' as const) : ('ok' as const);
}
const EXPIRY_CLASS = {
  expired: 'text-danger-600 dark:text-danger-500',
  soon: 'text-warning-600 dark:text-harvest-300',
  ok: 'text-muted',
  none: 'text-subtle',
} as const;

/**
 * WHICH PRODUCT A VARIANT BELONGS TO.
 *
 * Keyed on the product's NAME — the word the reader actually calls it ("GreenGro Bio"), the same word
 * every other screen prints. `group_code` is NOT used: it is filled in as a loose category here
 * ("Bio-Fertilizer" carries two unrelated products), so grouping on it would offer two pickable
 * products with the same label and hide a product under a category nobody searches for.
 */
const familyKey = (r: any) => String(r?.productName ?? '').trim().toLowerCase() || String(r?.sku ?? r?.productId ?? '');

/** "3 expired" in red, or the quiet all-clear — the count a lot list is really being scanned for. */
function ExpiryNote({ expired, lots }: { expired: number; lots: number }) {
  if (expired > 0) {
    return <span className="type-caption text-danger-600 dark:text-danger-500">{expired} expired</span>;
  }
  return <span className="type-caption text-subtle">{lots} {lots === 1 ? 'lot' : 'lots'}, none expired</span>;
}

/** STEP 1 — a product the way the catalogue names it, with how much of it there is to trace. */
function ProductOption({ f }: { f: any }) {
  return (
    <span className="flex min-w-0 items-center gap-2.5 py-0.5">
      <EntityAvatar name={f.name} src={f.imageUrl} square size={30} icon={<Package size={14} />} />
      <span className="min-w-0 flex-1">
        <b className="block truncate">{f.name}</b>
        <span className="mt-0.5 block truncate type-caption text-muted">
          {f.variants.length} {f.variants.length === 1 ? 'variant' : 'variants'} · {f.lots} {f.lots === 1 ? 'lot' : 'lots'}
        </span>
      </span>
      <span className="shrink-0 text-right">
        <span className="block type-label-strong tabular-nums">
          {fmtQty(f.onHand)} <span className="type-caption text-muted">{f.uomCode}</span>
        </span>
        <span className="mt-0.5 block"><ExpiryNote expired={f.expiredLots} lots={f.lots} /></span>
      </span>
    </span>
  );
}

/** STEP 2 — the pack. The SKU rides along because two variants can share a label ("1L" twice). */
function VariantOption({ v }: { v: any }) {
  return (
    <span className="flex min-w-0 items-center gap-2.5 py-0.5">
      <EntityAvatar name={v.variantLabel ?? v.sku} src={v.imageUrl} square size={30} icon={<Layers size={14} />} />
      <span className="min-w-0 flex-1">
        <span className="flex min-w-0 flex-wrap items-center gap-1.5">
          <b className="truncate">{v.variantLabel || 'Standard pack'}</b>
          {!Number(v.isActive) && <Tag>inactive</Tag>}
        </span>
        <span className="mt-0.5 block truncate type-caption text-muted">{v.sku}</span>
      </span>
      <span className="shrink-0 text-right">
        <span className="block type-label-strong tabular-nums">
          {fmtQty(v.onHand)} <span className="type-caption text-muted">{v.uomCode}</span>
        </span>
        <span className="mt-0.5 block"><ExpiryNote expired={Number(v.expiredLots ?? 0)} lots={Number(v.lots ?? 0)} /></span>
      </span>
    </span>
  );
}

/** The product a lot belongs to, said once — name and pack, never the SKU minted from them. */
const productLabel = (o: any) => [o?.productName, o?.variantLabel].filter(Boolean).join(' · ');

/** A lot's number, drawn as an IDENTIFIER (the app's CodeChip) and reddened once it has expired. */
function LotChip({ o, expired }: { o: any; expired: boolean }) {
  return (
    <CodeChip hue={expired ? PALETTE.danger[500] : undefined} title={o.batchNo}>{o.batchNo}</CodeChip>
  );
}

/** Marks that only appear when they apply: expired, returned lot, QC hold, blocked. */
function LotFlags({ o, expired }: { o: any; expired: boolean }) {
  const statuses = String(o.statuses ?? '').split(',').filter(Boolean);
  return (
    <>
      {expired && <Tag tone="red" icon={<TriangleAlert size={10} />}>expired</Tag>}
      {isReturnLot(o) && <Tag tone="gold" icon={<Undo2 size={10} />}>returned</Tag>}
      {/* EXPIRED is already said in red above — repeating the raw status beside it says it twice. */}
      {statuses.filter((st) => st !== 'AVAILABLE' && !(expired && st === 'EXPIRED')).map((st) => (
        <Tag key={st} tone={statusMeta(st).tone}>{statusMeta(st).label}</Tag>
      ))}
    </>
  );
}

/**
 * STEP 3 IN THE DROPDOWN — the batch number, and nothing else.
 *
 * The reader is choosing between NUMBERS. A photo of the product, its name, its pack, its quantity
 * and its unit are identical on every row of a list already narrowed to one product, so three rows
 * in a row all read "Biovin Powder · 10Kg" and the number — the only thing that differs — is what
 * gets crowded out. The single exception is expiry, because it changes the choice.
 */
function BatchNumberOption({ o }: { o: any }) {
  const expired = expiryState(o.expDate, o.statuses) === 'expired';
  return (
    <span className="flex min-w-0 flex-wrap items-center gap-1.5 py-0.5">
      <LotChip o={o} expired={expired} />
      {expired && <Tag tone="red" icon={<TriangleAlert size={10} />}>expired</Tag>}
    </span>
  );
}

/**
 * THE SAME LOT AS A CARD — where there IS room, so the card answers what a dropdown row cannot.
 *
 * A grid of bare numbers is a wall of strings: nothing says which of them still holds stock, which
 * one runs out first, or which came back from a customer. Every fact here VARIES from lot to lot,
 * which is the whole test — the product's name is the one thing that does not, so it appears only
 * when the list spans several products (no variant picked). The photo stays either way: it is what
 * makes a card a thing rather than an empty box.
 */
function BatchCard({ o, showProduct }: { o: any; showProduct: boolean }) {
  const onHand = Number(o.onHand ?? 0);
  const exp = expiryState(o.expDate, o.statuses);
  const expired = exp === 'expired';
  return (
    <span className="flex min-w-0 items-center gap-2.5">
      <EntityAvatar name={o.productName ?? o.batchNo} src={o.imageUrl} square size={36} icon={<Boxes size={16} />} />
      <span className="min-w-0 flex-1">
        <span className="flex min-w-0 flex-wrap items-center gap-1.5">
          <LotChip o={o} expired={expired} />
          <LotFlags o={o} expired={expired} />
        </span>
        <span className="mt-1 block truncate type-caption text-muted">
          {showProduct && <span className="text-content">{productLabel(o)} · </span>}
          {onHand > 0
            ? `${o.warehouses} ${Number(o.warehouses) === 1 ? 'warehouse' : 'warehouses'}`
            : 'none left'}
          {o.expDate && ' · '}
          {o.expDate && <span className={EXPIRY_CLASS[exp]}>exp {dayjs(o.expDate).format('MMM YYYY')}</span>}
        </span>
      </span>
      {onHand > 0 && (
        <span className="shrink-0 text-right type-label-strong tabular-nums">
          {fmtQty(onHand)} <span className="type-caption text-muted">{o.uomCode}</span>
        </span>
      )}
    </span>
  );
}

/** A numbered field label, so the three pickers read as one cascade instead of three filters. */
function StepLabel({ n, htmlFor, children }: { n: number; htmlFor: string; children: ReactNode }) {
  return (
    <label className="mb-1.5 flex items-center gap-1.5 type-label-strong text-content" htmlFor={htmlFor}>
      <span className="inline-flex size-[18px] shrink-0 items-center justify-center rounded-full bg-brand-100 type-caption-strong text-brand-700 dark:bg-brand-900/40 dark:text-brand-300">{n}</span>
      {children}
    </label>
  );
}

/**
 * Trace any batch number end to end: where the stock currently sits (per warehouse), how much
 * reached each retailer, and the full movement timeline (master add → transfers → dispatch → return).
 */
export function BatchTracePage() {
  // The pick is made in three steps — product, then variant, then lot — because a bare batch number
  // ("10KG-OPENING-377") identifies nothing a human recognises, and a flat list of every lot in the
  // warehouse asks the reader to recognise one. Narrowing by the product they already have in mind
  // turns the last step into a short list of that variant's own lots.
  const [productKey, setProductKey] = useState<string | undefined>();
  const [variantId, setVariantId] = useState<string | undefined>();
  // A batch_no can recur across products, so a selection is (batchNo, productId) — encoded as the
  // Select value "productId|batchNo".
  const [selected, setSelected] = useState<string | undefined>();
  const [searchInput, setSearchInput] = useState('');
  const search = useDebounce(searchInput, 350);
  const [productId, batchNo] = selected ? [selected.split('|')[0], selected.split('|').slice(1).join('|')] : [undefined, undefined];

  const { data: productRows, isFetching: productsLoading } = useQuery({
    queryKey: ['batch-trace-products'],
    queryFn: () => request<any[]>({ url: '/batch-trace/products' }),
  });

  const { data: options, isFetching: optionsLoading } = useQuery({
    // With a variant chosen the server returns EVERY lot that variant ever had (spent and expired
    // included); without one it stays the catalogue-wide number search, so a batch number typed
    // straight in still finds its lot.
    queryKey: ['batch-trace-options', search, variantId],
    queryFn: () => request<any[]>({ url: '/batch-trace/options', params: { q: search, productId: variantId } }),
  });

  /** Variants collapsed into the products they belong to — the list step 1 offers. */
  const families = useMemo(() => {
    const byKey = new Map<string, any>();
    for (const r of productRows ?? []) {
      const key = familyKey(r);
      let f = byKey.get(key);
      if (!f) {
        f = { key, name: r.productName ?? r.sku, imageUrl: r.imageUrl, uomCode: r.uomCode, variants: [], lots: 0, onHand: 0, expiredLots: 0 };
        byKey.set(key, f);
      }
      f.variants.push(r);
      f.lots += Number(r.lots ?? 0);
      f.onHand += Number(r.onHand ?? 0);
      f.expiredLots += Number(r.expiredLots ?? 0);
      if (!f.imageUrl && r.imageUrl) f.imageUrl = r.imageUrl;
    }
    return [...byKey.values()].sort((a, b) => String(a.name).localeCompare(String(b.name)));
  }, [productRows]);

  const family = useMemo(() => families.find((f) => f.key === productKey), [families, productKey]);

  const productSelectOptions = useMemo(
    () => families.map((f) => ({
      value: f.key,
      label: f.name,
      searchLabel: `${f.name} ${f.variants.map((v: any) => `${v.variantLabel ?? ''} ${v.sku ?? ''}`).join(' ')}`,
      data: f,
    })),
    [families],
  );

  const variantSelectOptions = useMemo(
    () => (family?.variants ?? []).map((v: any) => ({
      value: String(v.productId),
      label: v.variantLabel || 'Standard pack',
      searchLabel: `${v.variantLabel ?? ''} ${v.sku ?? ''}`,
      data: v,
    })),
    [family],
  );

  /** Picking a product restarts the cascade below it; a lone variant is filled in, not asked about. */
  const pickProduct = (key?: string) => {
    const f = families.find((x) => x.key === key);
    setProductKey(key);
    setVariantId(f && f.variants.length === 1 ? String(f.variants[0].productId) : undefined);
    setSelected(undefined);
    setSearchInput('');
  };
  const pickVariant = (id?: string) => { setVariantId(id); setSelected(undefined); setSearchInput(''); };
  /** A lot picked (or typed) directly still fills the two steps above it in, so the page says what it is. */
  const pickBatch = (v?: string) => {
    setSelected(v);
    setSearchInput('');
    if (!v) return;
    const pid = v.split('|')[0];
    const row = (productRows ?? []).find((r: any) => String(r.productId) === pid);
    if (row) { setProductKey(familyKey(row)); setVariantId(pid); }
  };

  const { data, isFetching } = useQuery({
    queryKey: ['batch-trace', batchNo, productId],
    queryFn: () => request<any>({ url: '/batch-trace', params: { batchNo, productId } }),
    enabled: !!batchNo && !!productId,
  });

  const selectOptions = useMemo(
    () => (options ?? []).map((o: any) => ({
      ...o,
      value: `${o.productId}|${o.batchNo}`,
      // The closed box shows the same chip the list showed, so the chosen lot still reads as a
      // batch number rather than turning back into a sentence. `title` keeps the plain text for
      // the trigger's hover and for search.
      text: (
        <span className="inline-flex min-w-0 max-w-full items-center align-middle">
          <CodeChip hue={expiryState(o.expDate, o.statuses) === 'expired' ? PALETTE.danger[500] : undefined}>{o.batchNo}</CodeChip>
        </span>
      ),
      label: o.batchNo,
      title: o.batchNo,
    })),
    [options],
  );
  // The grid answers whichever step is still open, so one cap serves all three. 24 fits eight rows
  // of three without turning the page into a scroll; anything past it is named, never silently cut.
  const GRID_CARDS = 24;
  const expiredInList = useMemo(
    () => selectOptions.filter((o: any) => expiryState(o.expDate, o.statuses) === 'expired').length,
    [selectOptions],
  );

  const product = (data?.products ?? [])[0];
  /**
   * THE LOT THAT WAS TRACED - its own status and its own dates.
   *
   * These were read across the whole FAMILY, which is a set of DIFFERENT lots: the printed batch plus
   * the RET- children minted from it when stock came back. Tracing the returned child
   * RET-EXPIRED-RET/341637983 therefore showed "Available" AND "Expired" side by side (Available
   * belongs to the emptied parent), and "Expires 06 Jul 2029 - past date", a date borrowed from the
   * parent and called past because the CHILD carried the EXPIRED flag. Two lots cannot share one
   * label. Each lot's own state is in "Where it is now", a row each; this panel speaks only for the
   * number at the top of it.
   */
  const lot = useMemo(() => {
    const rows = data?.byWarehouse ?? [];
    const own = rows.filter((r: any) => String(r.batchNo) === String(data?.batchNo));
    // An unknown number yields no rows at all; fall back rather than render a blank panel.
    const src = own.length ? own : rows;
    const dates = (f: string) => src.map((r: any) => r[f]).filter(Boolean).sort();
    const withStatus = src.filter((r: any) => r.status);
    const statuses = [...new Set(withStatus.map(effectiveStatus))] as string[];
    const returnedIn = (data?.timeline ?? [])
      .filter((t: any) => t.txnType === 'RETURN_IN')
      .reduce((a: number, t: any) => a + Math.abs(Number(t.qty ?? 0)), 0);
    return {
      mfgDate: dates('mfgDate')[0] ?? null,
      expDate: dates('expDate')[0] ?? null,
      statuses,
      /** One batch number CAN sit in two warehouses under different statuses - then say which is which. */
      statusSites: Object.fromEntries(statuses.map((st) => [
        st, withStatus.filter((r: any) => effectiveStatus(r) === st).map((r: any) => r.warehouseCode).filter(Boolean),
      ])) as Record<string, string[]>,
      flaggedExpired: statuses.includes('EXPIRED'),
      /** What the traced lot itself still holds — an AVAILABLE flag on zero stock decides nothing. */
      ownOnHand: src.reduce((a: number, r: any) => a + Number(r.available ?? 0), 0),
      /** Sibling lots of the same family, whose facts this panel deliberately does NOT speak for. */
      siblings: rows.length - src.length,
      heldBack: rows.filter(isReturnLot).reduce((a: number, r: any) => a + Number(r.available ?? 0), 0),
      returnedIn,
      returnMoves: (data?.timeline ?? []).filter((t: any) => t.txnType === 'RETURN_IN').length,
    };
  }, [data]);

  const uom = product?.uomCode ?? '';
  const exp = expiryState(lot.expDate, lot.flaggedExpired ? 'EXPIRED' : null);
  /** Flagged expired and PAST ITS DATE are two different claims - only the date may say "past date". */
  const expDatePast = !!lot.expDate && dayjs(lot.expDate).isValid() && dayjs(lot.expDate).isBefore(dayjs(), 'day');

  return (
    <div>
      <PageHeader
        icon={<ScanBarcode />}
        title="Batch Traceability"
        subtitle="Follow one lot end to end — where it came from, which warehouses hold it now, which retailers received it, and what came back."
      />

      {/* THE PICKER IS THE PAGE until something is chosen, so it gets its own panel rather than being a
          control tucked above an empty table. Product → variant → lot, left to right. */}
      <Card className="mb-4">
        <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-3">
          <div className="min-w-0">
            <StepLabel n={1} htmlFor="product-picker">Which product?</StepLabel>
            <Select
              id="product-picker"
              showSearch
              allowClear
              className="w-full"
              size="large"
              placeholder="Choose a product"
              value={productKey}
              loading={productsLoading}
              optionFilterProp="searchLabel"
              suffixIcon={<Package size={16} />}
              notFoundContent="No product has any lots"
              options={productSelectOptions}
              optionRender={(o: any) => <ProductOption f={o.data} />}
              onChange={(v) => pickProduct(v)}
            />
          </div>

          <div className="min-w-0">
            <StepLabel n={2} htmlFor="variant-picker">Which variant?</StepLabel>
            <Select
              id="variant-picker"
              showSearch
              allowClear
              className="w-full"
              size="large"
              disabled={!family}
              placeholder={family ? 'Choose a pack' : 'Pick a product first'}
              value={variantId}
              optionFilterProp="searchLabel"
              suffixIcon={<Layers size={16} />}
              notFoundContent="This product has no other pack"
              options={variantSelectOptions}
              optionRender={(o: any) => <VariantOption v={o.data} />}
              onChange={(v) => pickVariant(v)}
            />
          </div>

          <div className="min-w-0">
            <StepLabel n={3} htmlFor="batch-picker">Which lot?</StepLabel>
            <Select
              id="batch-picker"
              showSearch
              allowClear
              className="w-full"
              size="large"
              placeholder={variantId ? 'Choose a batch — expired ones are in red' : 'Type a batch number, e.g. 10KG-OPENING-377'}
              value={selected}
              loading={optionsLoading}
              onSearch={setSearchInput}
              onChange={(v) => pickBatch(v)}
              filterOption={false}
              optionLabelProp="text"
              suffixIcon={<Search size={16} />}
              notFoundContent={search
                ? `No lot matches “${search}”`
                : variantId ? 'No lots on record for this variant' : 'Start typing a batch number'}
              options={selectOptions}
              optionRender={(o: any) => <BatchNumberOption o={o.data ?? o} />}
            />
          </div>
        </div>
        <p className="mt-2 type-caption text-muted">
          {variantId
            ? <>Every lot this variant ever had — spent and <b className="text-danger-600 dark:text-danger-500">expired</b> ones included.</>
            : <>Know the number already? Type it straight into step 3 — it searches the printed batch number <b>and</b> the returned{' '}
              <code className="rounded bg-surface-3 px-1">RET-</code> lots minted from it, so a recall started from a customer return still shows the original dispatch.</>}
        </p>
      </Card>

      {/* Nothing picked yet: rather than an empty box, the grid MIRRORS whichever step is still
          unanswered — products, then that product's packs, then that pack's lots. It used to show
          "lots in stock right now", which was the first nine of a list sorted alphabetically by batch
          number: `10KG-…` and `1783…` beat every letter, so the page opened on three lots of one
          powder and two test products and read as though nothing else were in stock. */}
      {!batchNo && (
        <Card>
          <EmptyState
            icon={<ScanBarcode size={22} />}
            title={variantId ? 'Pick one of its lots' : family ? 'Pick a pack' : 'Pick a batch to trace it'}
            description="Every movement of a lot — receipt, transfer, dispatch, return and adjustment — with the warehouses holding it and the retailers who took it."
            compact
          />

          {/* STEP 1 unanswered → the products themselves. */}
          {!family && !!families.length && (
            <>
              <div className="mb-2 mt-1 type-overline text-subtle">Products with lots to trace</div>
              <div className="grid gap-2 sm:grid-cols-2 xl:grid-cols-3">
                {families.slice(0, GRID_CARDS).map((f: any) => (
                  <button
                    key={f.key} type="button" onClick={() => pickProduct(f.key)}
                    className="min-w-0 rounded-card border border-line bg-surface shadow-e1 px-3 py-2 text-left transition-colors hover:border-brand-300 hover:bg-brand-50/60 dark:hover:bg-brand-900/20"
                  >
                    <ProductOption f={f} />
                  </button>
                ))}
              </div>
              {families.length > GRID_CARDS && (
                <div className="mt-2 type-caption text-warning-600 dark:text-harvest-300">
                  Showing {GRID_CARDS} of {families.length} products — use <b>Which product?</b> above to reach the rest.
                </div>
              )}
            </>
          )}

          {/* STEP 2 unanswered → that product's packs. */}
          {family && !variantId && (
            <>
              <div className="mb-2 mt-1 type-overline text-subtle">Packs of {family.name}</div>
              <div className="grid gap-2 sm:grid-cols-2 xl:grid-cols-3">
                {family.variants.map((v: any) => (
                  <button
                    key={v.productId} type="button" onClick={() => pickVariant(String(v.productId))}
                    className="min-w-0 rounded-card border border-line bg-surface shadow-e1 px-3 py-2 text-left transition-colors hover:border-brand-300 hover:bg-brand-50/60 dark:hover:bg-brand-900/20"
                  >
                    <VariantOption v={v} />
                  </button>
                ))}
              </div>
            </>
          )}

          {/* STEP 3 unanswered → that pack's lots. */}
          {variantId && !!selectOptions.length && (
            <>
              <div className="mb-2 mt-1 flex flex-wrap items-baseline justify-between gap-x-3 gap-y-1">
                <span className="type-overline text-subtle">Every lot of this variant · soonest to expire first</span>
                {expiredInList > 0 && (
                  <span className="type-caption-strong text-danger-600 dark:text-danger-500">
                    <TriangleAlert size={11} className="mr-1 inline align-[-1px]" />
                    {expiredInList} of these {expiredInList === 1 ? 'lots has' : 'lots have'} expired
                  </span>
                )}
              </div>
              <div className="grid gap-2 sm:grid-cols-2 xl:grid-cols-3">
                {selectOptions.slice(0, GRID_CARDS).map((o: any) => {
                  const expired = expiryState(o.expDate, o.statuses) === 'expired';
                  return (
                    <button
                      key={o.value} type="button" onClick={() => pickBatch(o.value)}
                      /* `min-w-0`: a grid item defaults to `min-width:auto`, so it refuses to shrink below
                         its content. The card truncates inside, but the button itself would otherwise grow
                         to fit the longest batch label and push the whole PAGE past a 320px phone. */
                      className={`min-w-0 rounded-card border shadow-e1 px-3 py-2 text-left transition-colors ${expired
                        ? 'border-danger-500/40 bg-danger-500/5 hover:bg-danger-500/10'
                        : 'border-line bg-surface hover:border-brand-300 hover:bg-brand-50/60 dark:hover:bg-brand-900/20'}`}
                    >
                      <BatchCard o={o} showProduct={false} />
                    </button>
                  );
                })}
              </div>
              {/* Say what is NOT on screen — a grid that silently stops reads as "that is all of them". */}
              {selectOptions.length > GRID_CARDS && (
                <div className="mt-2 type-caption text-warning-600 dark:text-harvest-300">
                  Showing {GRID_CARDS} of this variant’s {selectOptions.length} lots — use <b>Which lot?</b> above to reach the rest.
                </div>
              )}
            </>
          )}
        </Card>
      )}

      {batchNo && data && (
        <>
          {/* IDENTITY — the product on the left, the LOT on the right. They are two different things
              and were previously run together, so the batch number (the thing being traced) sat in the
              corner as an afterthought. */}
          <Card className="mb-4">
            <div className="flex flex-wrap items-start gap-5">
              {productImage(product) ? (
                <Image src={productImage(product)} width={104} height={104} className="rounded-card object-cover" preview />
              ) : (
                <EntityAvatar name={product?.productName ?? batchNo} square size={104} icon={<Boxes size={34} />} />
              )}
              <div className="min-w-[240px] flex-1">
                <div className="type-section-title">
                  {product?.productName ?? 'Unknown product'}
                  {product?.variantLabel ? <span className="text-muted"> · {product.variantLabel}</span> : null}
                </div>
                <div className="mt-2 flex flex-wrap items-center gap-1.5">
                  {product?.sku && !isRedundantSku(product.sku, product.productName, product.variantLabel) && <Tag>SKU {product.sku}</Tag>}
                  {product?.hsnCode ? <Tag>HSN {product.hsnCode}</Tag> : null}
                  {Number(product?.packSize) ? <Tag>Pack {fmtQty(product.packSize)} {product.uomCode}</Tag> : <Tag>UoM {product?.uomCode}</Tag>}
                  {Number(product?.boxPackingEnabled) && Number(product?.unitsPerBox) > 0 ? <Tag tone="gold">Box of {product.unitsPerBox}</Tag> : null}
                  {Number(product?.gstPct) > 0 ? <Tag>GST {Number(product.gstPct)}%</Tag> : null}
                  {Number(product?.dealerPrice) > 0 ? <Tag tone="blue">Dealer {inr2(product.dealerPrice)}</Tag> : null}
                </div>
              </div>

              <div className="min-w-[230px] rounded-card border border-line bg-surface-2 px-3 py-2.5">
                <div className="type-overline text-subtle">Batch / lot</div>
                <div className="mt-0.5 type-body-strong tabular-nums">{data.batchNo}</div>
                {/* A returned child lot is traced together with its parent — say which one is the root,
                    or the totals below look like they belong to the number that was typed. */}
                {data.root && data.root !== data.batchNo && (
                  <div className="mt-1 type-caption text-muted">
                    <Undo2 size={11} className="mr-1 inline align-[-1px]" />
                    part of the family printed as <b className="tabular-nums">{data.root}</b>
                  </div>
                )}
                <div className="mt-2 flex flex-wrap items-center gap-1.5">
                  {lot.statuses.map((s) => (
                    <Tag key={s} tone={s === 'AVAILABLE' ? sellableVerdict(s, lot.ownOnHand).tone : statusMeta(s).tone}>
                      {s === 'AVAILABLE' && !(lot.ownOnHand > 0) ? 'Emptied' : statusMeta(s).label}
                      {/* Two statuses on ONE number means two warehouses disagree - name them, or the
                          pair reads as two states of the same stock. */}
                      {lot.statuses.length > 1 && lot.statusSites[s]?.length ? ` · ${lot.statusSites[s].join(', ')}` : ''}
                    </Tag>
                  ))}
                </div>
                <div className="mt-2 space-y-0.5 type-caption">
                  <div className="text-muted">
                    <CalendarClock size={11} className="mr-1 inline align-[-1px]" />
                    Made {lot.mfgDate ? formatIstDate(lot.mfgDate) : <span className="text-subtle">not recorded</span>}
                  </div>
                  <div className={lot.expDate || lot.flaggedExpired ? EXPIRY_CLASS[exp] : 'text-subtle'}>
                    {exp === 'expired' && <TriangleAlert size={11} className="mr-1 inline align-[-1px]" />}
                    Expires {lot.expDate ? formatIstDate(lot.expDate) : 'not recorded'}
                    {expDatePast && ' · past date'}
                    {/* Flagged by hand with no date, or with a date still to come: say which it is
                        rather than asserting a date has passed when it has not. */}
                    {!expDatePast && lot.flaggedExpired && ' · flagged expired'}
                    {exp === 'soon' && ` · in ${dayjs(lot.expDate).diff(dayjs(), 'day')} days`}
                  </div>
                  {lot.siblings > 0 && (
                    <div className="text-subtle">
                      <Undo2 size={11} className="mr-1 inline align-[-1px]" />
                      {lot.siblings} other {lot.siblings === 1 ? 'lot' : 'lots'} in this family - each with its own
                      status and dates, listed under <b>Where it is now</b>.
                    </div>
                  )}
                </div>
              </div>
            </div>
          </Card>

          {data.scoped && (
            <div className="mb-3 flex items-start gap-2 rounded-control border border-sky-500/25 bg-sky-500/10 px-3 py-2 type-caption text-content">
              <ShieldAlert size={14} className="mt-0.5 shrink-0 text-sky-600 dark:text-sky-400" />
              <span>Showing only the warehouse(s) you manage — on-hand, movements and the retailers you dispatched to. Admins see all warehouses.</span>
            </div>
          )}

          <div className="mb-4 grid grid-cols-2 gap-3 md:grid-cols-3 xl:grid-cols-5">
            <StatCard
              tone="brand" icon={<PackageCheck size={18} />}
              label={data.scoped ? 'On hand (your warehouse)' : 'On hand (all warehouses)'}
              value={<span className="tabular-nums">{fmtQty(data.summary.totalAvailable)} <span className="type-body text-muted">{uom}</span></span>}
              hint={`still in stock from this lot${lot.heldBack > 0 ? ` · ${fmtQty(lot.heldBack)} of it returned stock` : ''}`}
            />
            <StatCard
              tone="sky" icon={<Store size={18} />}
              label={data.scoped ? 'Dispatched from you' : 'Dispatched to retailers'}
              value={<span className="tabular-nums">{fmtQty(data.summary.totalToRetailers)} <span className="type-body text-muted">{uom}</span></span>}
              hint={`${data.summary.retailers} ${data.summary.retailers === 1 ? 'retailer' : 'retailers'} reached${(data.byRetailer ?? []).length ? ` over ${(data.byRetailer ?? []).length} orders` : ''}`}
            />
            <StatCard
              tone={lot.returnedIn > 0 ? 'gold' : 'neutral'} icon={<Undo2 size={18} />}
              label="Came back"
              value={<span className="tabular-nums">{fmtQty(lot.returnedIn)} <span className="type-body text-muted">{uom}</span></span>}
              hint={lot.returnedIn > 0
                ? `${lot.returnMoves} return ${lot.returnMoves === 1 ? 'movement' : 'movements'} · ${fmtQty(lot.heldBack)} still held`
                : 'nothing returned from this lot'}
            />
            <StatCard
              tone="neutral" icon={<WarehouseIcon size={18} />} label="Warehouses holding it"
              value={data.summary.warehouses}
              hint={(data.byWarehouse ?? []).length > data.summary.warehouses
                ? `${(data.byWarehouse ?? []).length} lot rows, incl. emptied ones`
                : 'sites with stock left'}
            />
            <StatCard
              tone="neutral" icon={<Boxes size={18} />} label="Movements on record"
              value={(data.timeline ?? []).length}
              hint={(data.timeline ?? []).length ? `since ${formatIstDate((data.timeline ?? [])[0]?.txnDate)}` : 'no movements yet'}
            />
          </div>

          {/* `items-start`: the two tables rarely have the same number of rows, and stretching the
              shorter one to match left a card half empty. */}
          <div className="grid items-start gap-3 xl:grid-cols-2">
            <Card title="Where it is now">
              <Table size="small" rowKey={(_: any, i?: number) => String(i)} pagination={false} loading={isFetching} dataSource={data.byWarehouse ?? []}
                locale={{ emptyText: 'Not in any warehouse' }}
                columns={[
                  {
                    title: 'Warehouse', dataIndex: 'warehouseName',
                    render: (v: string, r: any) => (
                      <div className="flex min-w-0 items-center gap-2">
                        {/* No mark: a Warehouse glyph on every row (constant glyph on every row is not identity — see the colour-sweep brief). */}
                        <div className="min-w-0">
                          <div className="flex min-w-0 items-center">
                            <b className="truncate">{r.warehouseCode}</b>
                            {Number(r.isMaster) ? <MasterTag /> : null}
                          </div>
                          <div className="truncate type-caption text-muted">{v}</div>
                        </div>
                      </div>
                    ),
                  },
                  {
                    // Both lots wear the same identifier treatment as the picker above — the returned
                    // one used to be small grey text under its tag, so the two rows looked like a row
                    // and a footnote rather than two lots standing side by side.
                    title: 'Lot', dataIndex: 'batchNo',
                    render: (v: string, r: any) => (
                      <span className="flex min-w-0 flex-wrap items-center gap-1.5">
                        <CodeChip title={v}>{v}</CodeChip>
                        {isReturnLot(r) && (
                          <Tooltip label={`Internal lot for returned / quarantined stock${r.sourceBatchNo ? ` · printed as ${r.sourceBatchNo}` : ''}`}>
                            <Tag tone="gold" icon={<Undo2 size={10} />}>returned</Tag>
                          </Tooltip>
                        )}
                      </span>
                    ),
                  },
                  {
                    // The unit is said ONCE, in the heading — it is the same on every row of a trace.
                    title: uom ? `On hand (${uom})` : 'On hand', dataIndex: 'available', align: 'right' as const,
                    render: (v: any) => (Number(v) > 0
                      ? <b className="tabular-nums">{fmtQty(v)}</b>
                      : <span className="type-caption text-subtle">nothing left</span>),
                  },
                  {
                    title: 'Expiry', dataIndex: 'expDate',
                    render: (v: any, r: any) => {
                      if (!v) return <span className="type-caption text-subtle">not recorded</span>;
                      const e = expiryState(v, r.status);
                      return (
                        <span className={`whitespace-nowrap ${EXPIRY_CLASS[e]}`}>
                          {e === 'expired' && <TriangleAlert size={11} className="mr-1 inline align-[-1px]" />}
                          {formatIstDate(v)}
                        </span>
                      );
                    },
                  },
                  {
                    title: 'Sellable', dataIndex: 'status',
                    render: (v: string, r: any) => {
                      const verdict = sellableVerdict(v, r.available, r.expDate);
                      const tag = <Tag tone={verdict.tone}>{verdict.label}</Tag>;
                      return verdict.note ? <Tooltip label={verdict.note}><span>{tag}</span></Tooltip> : tag;
                    },
                  },
                ]} />
              <div className="mt-2 type-caption text-muted">
                One row per lot per warehouse. <b>Sellable</b> is the lot's own condition — <i>Emptied</i>
                {' '}means every unit has already left this warehouse, not that the lot was rejected.
              </div>
            </Card>

            <Card title="Who received it">
              <Table size="small" rowKey={(_: any, i?: number) => String(i)} pagination={false} dataSource={data.byRetailer ?? []}
                locale={{ emptyText: 'Not dispatched to any retailer yet' }}
                columns={[
                  {
                    title: 'Retailer', dataIndex: 'retailerName',
                    // No mark: a Store glyph on every row is not identity (see the colour-sweep brief).
                    // Name and code share a line: on a recall this cell is read to be dialled and
                    // quoted, not scanned down a column.
                    render: (v: string, r: any) => (
                      <div className="flex min-w-0 items-baseline gap-1.5">
                        <RetailerLink id={r.retailerId} className="truncate">{v}</RetailerLink>
                        {r.retailerCode && <span className="shrink-0 type-caption text-muted">{r.retailerCode}</span>}
                      </div>
                    ),
                  },
                  {
                    title: 'Order', dataIndex: 'orderNo',
                    render: (v: string, r: any) => (r.orderId
                      ? <Link to={`/orders/${r.orderId}`} className="font-medium text-brand-700 hover:underline dark:text-brand-400">{v}</Link>
                      : (v ?? dash)),
                  },
                  { title: 'Dispatched', dataIndex: 'dispatchedAt', render: (v: any) => (v ? <span className="whitespace-nowrap">{formatIstDate(v)}</span> : dash) },
                  { title: 'Qty', dataIndex: 'qty', align: 'right' as const, render: (v: any) => <b className="tabular-nums">{fmtQty(v)}</b> },
                ]} />
            </Card>
          </div>

          <Card title="Movement history" className="mt-3">
            <Table size="small" rowKey="id" pagination={{ pageSize: 25 }} dataSource={data.timeline ?? []}
              locale={{ emptyText: 'No movements' }}
              columns={[
                { title: 'When', dataIndex: 'txnDate', render: (v: string) => <span className="whitespace-nowrap">{formatIst(v)}</span> },
                {
                  title: 'Movement', dataIndex: 'txnType',
                  render: (v: string, r: any) => (
                    <span className="inline-flex items-center gap-1.5">
                      {Number(r.qty) < 0
                        ? <ArrowUpRight size={13} className="text-danger-600 dark:text-danger-500" />
                        : <ArrowDownRight size={13} className="text-brand-600 dark:text-brand-400" />}
                      <Tag tone={TXN_COLOR[v] ?? 'neutral'}>{txnLabel(v)}</Tag>
                    </span>
                  ),
                },
                {
                  // In a FAMILY trace the rows come from several lots (the original plus its returned
                  // children), so the row has to say which one moved.
                  title: 'Lot', dataIndex: 'batchNo',
                  render: (v: string, r: any) => (
                    <span className="inline-flex items-center gap-1.5">
                      <span className="tabular-nums">{v}</span>
                      {isReturnLot(r) && <Tooltip label="Returned / quarantined lot"><Tag tone="gold">ret</Tag></Tooltip>}
                    </span>
                  ),
                },
                { title: 'Warehouse', dataIndex: 'warehouseCode', render: (v: any) => v ?? dash },
                {
                  title: 'Qty', dataIndex: 'qty', align: 'right' as const,
                  render: (v: any) => {
                    const n = Number(v);
                    return (
                      <b className={`tabular-nums ${n < 0 ? 'text-danger-600 dark:text-danger-500' : 'text-brand-700 dark:text-brand-400'}`}>
                        {n > 0 ? '+' : ''}{fmtQty(v)}
                      </b>
                    );
                  },
                },
                { title: 'Balance after', dataIndex: 'runningBalance', align: 'right' as const, render: (v: any) => <span className="tabular-nums text-muted">{fmtQty(v)}</span> },
                {
                  title: 'Document',
                  render: (_: any, r: any) => (r.refNo
                    ? <span className="type-caption">{r.refNo}</span>
                    : <span className="type-caption text-subtle">{r.refTable ? String(r.refTable).replace(/_/g, ' ') : '—'}</span>),
                },
              ]} />
          </Card>
        </>
      )}
    </div>
  );
}
